import ImageDimensions from '@/model/ImageDimensions';
import Blitz from 'blitz-resize';
import loadImage from './loadImage';
import runWorker from './runWorker';

export default {
    async generate(file: File, thumbSize: ImageDimensions, signal?: AbortSignal): Promise<File> {
        const image = await loadImage(file, signal);
        const canvas = document.createElement('canvas');
        try {
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            const context = canvas.getContext('2d')!;
            context.drawImage(image, 0, 0);
            image.removeAttribute('src');
            const { width, height } = thumbSize;
            if (canvas.width !== width || canvas.height !== height) {
                const original = context.getImageData(0, 0, canvas.width, canvas.height);
                const output = new Uint8ClampedArray(width * height * 4);
                // Keep Blitz's exact Hermite filter; own its worker lifecycle instead of Blitz.resize.
                const url = Blitz._workerBlobURL();
                try {
                    const result = await runWorker<{ data: ArrayBuffer }>(new Worker(url), [
                        original.data.buffer, canvas.width, canvas.height, width, height, output.buffer,
                    ], [original.data.buffer, output.buffer], signal);
                    canvas.width = width;
                    canvas.height = height;
                    context.putImageData(new ImageData(new Uint8ClampedArray(result.data), width, height), 0, 0);
                } finally { URL.revokeObjectURL(url); }
            }
            const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob(
                result => result ? resolve(result) : reject(new Error('Unable to encode thumbnail')),
                'image/jpeg', 0.7,
            ));
            if (signal?.aborted) throw new DOMException('Upload cancelled', 'AbortError');
            return new File([blob], file.name, { type: 'image/jpeg' });
        } finally {
            image.removeAttribute('src');
            canvas.width = canvas.height = 0;
        }
    },
};
